import { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const WEDDING_DATE = new Date("2027-05-22T16:00:00+02:00");
const HERO_IMG =
  "https://images.unsplash.com/photo-1770466704642-f38c433e1c8e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHw0fHxmbGlnaHQlMjBob3Jpem9uJTIwY2xvdWQlMjBza3klMjB2aWV3fGVufDB8fHx8MTc4ODI2NDA1N3ww&ixlib=rb-4.1.0&q=85";
const ROUTE = "M 20 250 C 260 60, 520 300, 760 120 C 920 10, 1080 170, 1180 60";

const lineReveal = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.15, ease: [0.16, 1, 0.3, 1], delay: 0.35 + i * 0.16 },
  }),
};

function useCountdown() {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, WEDDING_DATE.getTime() - now);
  return {
    Giorni: Math.floor(diff / 86400000),
    Ore: Math.floor(diff / 3600000) % 24,
    Minuti: Math.floor(diff / 60000) % 60,
    Secondi: Math.floor(diff / 1000) % 60,
  };
}

export default function Hero({ onNavigate }) {
  const t = useCountdown();
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 700], [0, 110]);
  const yText = useTransform(scrollY, [0, 700], [0, -60]);

  return (
    <section id="top" className="relative min-h-screen flex items-center pt-24 pb-28 overflow-hidden" data-testid="hero-section">
      <svg
        viewBox="0 0 1200 320"
        preserveAspectRatio="none"
        className="absolute bottom-6 left-0 w-full h-64 pointer-events-none opacity-80"
        fill="none"
        aria-hidden="true"
      >
        <path d={ROUTE} className="flight-path" stroke="#6E7E65" strokeWidth="1.5" />
        <path d="M0 0 L16 6 L0 12 L4 6 Z" fill="#C86D51">
          <animateMotion dur="16s" repeatCount="indefinite" rotate="auto" path={ROUTE} />
        </path>
      </svg>

      <div className="relative z-10 mx-auto max-w-7xl px-6 sm:px-12 lg:px-24 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center w-full">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="inline-flex items-center gap-3 border border-sand rounded-full px-4 py-2 bg-cream mb-10"
            data-testid="hero-boarding-badge"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-terracotta animate-pulse" />
            <span className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.25em] text-clay">
              Volo AF-2027 · Destinazione: Per Sempre
            </span>
          </motion.div>

          <h1 className="font-serif tracking-tight text-[#2D2A26]" data-testid="hero-title">
            <span className="block overflow-hidden pb-1">
              <motion.span
                custom={0}
                variants={lineReveal}
                initial="hidden"
                animate="show"
                className="block text-5xl sm:text-7xl lg:text-8xl leading-[0.95]"
              >
                Federica
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span
                custom={1}
                variants={lineReveal}
                initial="hidden"
                animate="show"
                className="block text-5xl sm:text-7xl lg:text-8xl leading-[0.95]"
              >
                <span className="italic text-terracotta">&amp;</span> Alessandro
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85 }}
            className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-[#5C564E]"
            data-testid="hero-subtitle"
          >
            Una fisioterapista ed un pilota in rotta verso la loro avventura più
            bella.
            <br />
            Saremo felici di avervi con noi{" "}
            <span className="font-medium text-[#2D2A26]">Sabato 22 Maggio 2027</span>{" "}
            a Grottaferrata, nel cuore millenario dei Castelli Romani.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05 }}
            className="mt-10 flex flex-wrap gap-3"
            data-testid="countdown"
          >
            {Object.entries(t).map(([label, value]) => (
              <div
                key={label}
                className="min-w-[76px] border border-sand rounded-2xl px-4 py-3 bg-cream text-center"
                data-testid={`countdown-${label.toLowerCase()}`}
              >
                <div className="font-mono text-2xl sm:text-3xl text-[#2D2A26] tabular-nums">
                  {String(value).padStart(2, "0")}
                </div>
                <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-clay mt-1">
                  {label}
                </div>
              </div>
            ))}
          </motion.div>

          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.25 }}
            onClick={() => onNavigate("rsvp")}
            className="mt-10 inline-flex items-center gap-3 rounded-full bg-charcoal text-panna font-mono text-[11px] uppercase tracking-[0.25em] px-7 py-4 transition-all duration-300 hover:bg-terracotta hover:gap-5"
            data-testid="hero-rsvp-cta"
          >
            Conferma la tua presenza
            <ArrowDown className="w-4 h-4" />
          </motion.button>
        </motion.div>

        <motion.div style={{ y: yImg }} className="lg:col-span-5 hidden lg:block">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="absolute -inset-4 rounded-t-[12rem] rounded-b-[2rem] bg-sand/40 -rotate-2" aria-hidden="true" />
            <div className="relative overflow-hidden rounded-t-[12rem] rounded-b-[2rem] border border-sand shadow-[0_30px_80px_-30px_rgba(28,25,23,0.35)]">
              <img
                src={HERO_IMG}
                alt="Vista dal finestrino di un aereo sopra le nuvole"
                className="w-full h-[540px] object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/30 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-panna/90">
                <span>Quota: 43.000 ft</span>
                <span>Rotta: Cuore → Cuore</span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
